import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Box, Flex } from "../../../../styled-system/jsx";
import type { ReportSummary } from "../model";
import { SEQUENTIAL_RAMP, TEAM_COLOR } from "../theme";
import { useActivityLabels } from "../useActivityLabels";
import { EmptyNote } from "./ActivityCharts";
import { Pill, Section } from "./primitives";

export function FeatureAdoption({ summary }: { summary: ReportSummary }) {
  const { t } = useTranslation(["user_activity"]);
  const labels = useActivityLabels();
  const denominator = Math.max(summary.peopleWithAccess, ...summary.features.map((feature) => feature.people), 1);

  const used = summary.features
    .filter((feature) => feature.people > 0)
    .sort((a, b) => b.people - a.people || b.actions - a.actions);
  const unused = summary.features.filter((feature) => feature.people === 0);

  return (
    <Section
      title={t(($) => $.user_activity["What they use"])}
      description={t(($) => $.user_activity["How many people touched each part of Trellis in this period."])}
    >
      {used.length === 0 ?
        <EmptyNote>{t(($) => $.user_activity["Nobody used Trellis in this period."])}</EmptyNote>
      : <ul className={css({ display: "flex", flexDirection: "column", gap: "14px", margin: 0, padding: 0 })}>
          {used.map((feature) => (
            <li key={feature.id} className={css({ listStyle: "none" })}>
              <Flex justifyContent="space-between" gap="small" marginBottom="6px" fontSize="medium">
                <span className={css({ color: "text.heading", fontWeight: "600" })}>{labels.feature(feature.id)}</span>
                <span className={css({ color: "text.secondary", fontVariantNumeric: "tabular-nums" })}>
                  {t(($) => $.user_activity["{people} of {total} people · {actions} actions"], {
                    people: feature.people,
                    total: denominator,
                    actions: feature.actions,
                  })}
                </span>
              </Flex>
              <Box
                height="8px"
                borderRadius="pill"
                overflow="hidden"
                style={{ backgroundColor: SEQUENTIAL_RAMP[0] }}
                role="meter"
                aria-valuemin={0}
                aria-valuemax={denominator}
                aria-valuenow={feature.people}
                aria-label={labels.feature(feature.id)}
              >
                <Box
                  height="100%"
                  borderRadius="pill"
                  style={{ width: `${(feature.people / denominator) * 100}%`, backgroundColor: TEAM_COLOR }}
                />
              </Box>
            </li>
          ))}
        </ul>
      }
      {unused.length > 0 && (
        <Box borderTop="1px solid" borderColor="border.default" paddingTop="medium">
          <p className={css({ margin: 0, marginBottom: "small", color: "text.secondary", fontSize: "small" })}>
            {t(
              ($) => $.user_activity["Nobody used these in this period — a chance to show them what they're missing:"],
            )}
          </p>
          <Flex gap="small" flexWrap="wrap">
            {unused.map((feature) => (
              <Pill key={feature.id}>{labels.feature(feature.id)}</Pill>
            ))}
          </Flex>
        </Box>
      )}
    </Section>
  );
}

function rampStep(value: number, max: number) {
  if (value <= 0 || max <= 0) return SEQUENTIAL_RAMP[0]!;
  const steps = SEQUENTIAL_RAMP.length - 1;
  return SEQUENTIAL_RAMP[Math.min(steps, 1 + Math.floor((value / max) * (steps - 1)))]!;
}

export function WorkingHoursHeatmap({ summary }: { summary: ReportSummary }) {
  const { t, i18n } = useTranslation(["user_activity"]);

  const { weekdays, hourLabel } = useMemo(() => {
    const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: "short" });
    const longWeekday = new Intl.DateTimeFormat(i18n.language, { weekday: "long" });
    const hour = new Intl.DateTimeFormat(i18n.language, { hour: "numeric" });
    // 2024-01-01 was a Monday, so these are Monday-first, matching the summary's rows.
    const monday = new Date(2024, 0, 1);
    return {
      weekdays: Array.from({ length: 7 }, (_, index) => {
        const date = new Date(monday.getFullYear(), 0, 1 + index);
        return { short: weekday.format(date), long: longWeekday.format(date) };
      }),
      hourLabel: (value: number) => hour.format(new Date(2024, 0, 1, value)),
    };
  }, [i18n.language]);

  const max = Math.max(...summary.heatmap.flat());
  const busiest = useMemo(() => {
    const cells = summary.heatmap.flatMap((row, day) => row.map((value, hour) => ({ day, hour, value })));
    return cells.reduce<(typeof cells)[number] | null>(
      (best, cell) => (cell.value > 0 && (!best || cell.value > best.value) ? cell : best),
      null,
    );
  }, [summary.heatmap]);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Section
      title={t(($) => $.user_activity["When they work"])}
      description={
        busiest ?
          t(($) => $.user_activity["Busiest around {hour} on {day}s. Times are in your time zone ({timeZone})."], {
            hour: hourLabel(busiest.hour),
            day: weekdays[busiest.day]!.long,
            timeZone,
          })
        : t(($) => $.user_activity["Times are in your time zone ({timeZone})."], { timeZone })
      }
    >
      {max === 0 ?
        <EmptyNote>{t(($) => $.user_activity["Nobody used Trellis in this period."])}</EmptyNote>
      : <Box overflowX="auto">
          <table
            className={css({
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: "3px",
              width: "100%",
              minWidth: "620px",
            })}
          >
            <caption className={css({ srOnly: true })}>
              {t(($) => $.user_activity["Actions by day of week and hour"])}
            </caption>
            <colgroup>
              <col className={css({ width: "48px" })} />
              {Array.from({ length: 24 }, (_, hour) => (
                <col key={hour} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th />
                {Array.from({ length: 24 }, (_, hour) => (
                  <th
                    key={hour}
                    scope="col"
                    className={css({
                      color: "text.secondary",
                      fontSize: "xsmall",
                      fontWeight: "500",
                      textAlign: "start",
                      whiteSpace: "nowrap",
                      overflow: "visible",
                    })}
                  >
                    {hour % 3 === 0 ?
                      hourLabel(hour)
                    : <span className={css({ srOnly: true })}>{hourLabel(hour)}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.heatmap.map((row, day) => (
                <tr key={day}>
                  <th
                    scope="row"
                    className={css({
                      paddingRight: "small",
                      color: "text.secondary",
                      fontSize: "small",
                      fontWeight: "500",
                      textAlign: "start",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {weekdays[day]!.short}
                  </th>
                  {row.map((value, hour) => {
                    const description = t(($) => $.user_activity["{day} {hour}: {count} actions"], {
                      day: weekdays[day]!.long,
                      hour: hourLabel(hour),
                      count: value,
                    });
                    return (
                      <td
                        key={hour}
                        title={description}
                        className={css({ borderRadius: "4px", height: "26px", padding: 0 })}
                        style={{ backgroundColor: rampStep(value, max) }}
                      >
                        <span className={css({ srOnly: true })}>{description}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <Flex
            justifyContent="flex-end"
            alignItems="center"
            gap="6px"
            marginTop="small"
            fontSize="xsmall"
            color="text.secondary"
          >
            {t(($) => $.user_activity["Less"])}
            {SEQUENTIAL_RAMP.map((step) => (
              <Box key={step} width="14px" height="14px" borderRadius="3px" style={{ backgroundColor: step }} />
            ))}
            {t(($) => $.user_activity["More"])}
          </Flex>
        </Box>
      }
    </Section>
  );
}
